import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { motion } from "motion/react";
import { useRouterState } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/hooks/use-session";
import { useHasStoredSession } from "@/hooks/use-stored-session";
import { GuestFeatureScreen } from "@/components/marketing/guest-feature-screen";
import { MarketingHome } from "@/components/marketing/marketing-home";
import { addChapter } from "@/lib/chapters.functions";
import { notifySaved, trackSave } from "@/lib/save-feedback";
import { useOverlayOpen } from "@/lib/overlay-store";
import { usePremium } from "@/components/billing/premium-lock";
import { useQuotaUpgrade } from "@/components/billing/use-quota-upgrade";
import type { Subject } from "@/lib/app-types";
import { EXAMS } from "@/lib/exams";
import {
  LECTURE_REVISION_INTERVALS,
  addDays,
  type StudyChapter,
} from "@/lib/lecture-plan";
import { onToastsNeeded, preloadToasts } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { WelcomeScreen } from "./welcome-screen";
import { BottomNav } from "./bottom-nav";
import { DesktopNav } from "./desktop-nav";
import { FloatingModeButton } from "./floating-mode-button";
import { SplashScreen } from "./splash-screen";
import { SiteFooter } from "./site-footer";
import { AddChapterSheet } from "./add-chapter-sheet";
import { Celebration } from "./celebration";
import { RippleButton } from "./ripple-button";
import { ThemeToggle } from "./theme-toggle";
import { GlobalQuoteSection } from "./global-quote-section";
import { InstallPrompt } from "./install-prompt";
import { FloatingProfileHeaderButton } from "./profile-header-button";



/** Toast runtime is loaded off the critical path — see src/lib/toast.ts. */
const Toaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

const StudyActionsContext = createContext<{ openAddChapter: () => void }>({
  openAddChapter: () => {},
});

export function useStudyActions() {
  return useContext(StudyActionsContext);
}

const WELCOME_KEY = "stp_seen_welcome";
const SPLASH_KEY = "stp_splash_shown";

/** Public marketing/content routes render for everyone, including crawlers. */
const PUBLIC_ROUTES = [
  "/jee-preparation-tracker",
  "/features",
  "/pricing",
  "/faq",
  "/test-tracker",
  "/revision-tracker",
  "/study-planner",
  "/community",
  "/community/about",
  "/community/social",
  "/community/owner",
  ...EXAMS.map((exam) => `/${exam.slug}`),
];

/**
 * Routes that read personal data. Signed-out visitors get a real 200 landing
 * page here instead of a login wall, so Google never hits a redirect.
 */
const GUEST_LANDINGS: {
  prefix: string;
  props: React.ComponentProps<typeof GuestFeatureScreen>;
}[] = [
  {
    prefix: "/tests",
    props: {
      eyebrow: "JEE test tracker",
      title: "Track every test and score",
      description:
        "Create a test, get a day-by-day revision and practice schedule, then log Physics, Chemistry and Maths marks to see averages, improvement and weak subjects.",
      bullets: [
        "Unlimited tests, each with its own schedule",
        "Automatic pre-test revision and practice tasks",
        "Subject-wise marks with validated totals",
        "Score history, averages and improvement trend",
      ],
      explore: [
        { to: "/test-tracker", label: "How the test tracker works" },
        { to: "/features", label: "All features" },
      ],
    },
  },
  {
    prefix: "/revisions",
    props: {
      eyebrow: "JEE revision planner",
      title: "Revision that schedules itself",
      description:
        "Mark a lecture complete and spaced revisions are queued automatically. Each day you get one honest list of what is due, with backlog carried forward.",
      bullets: [
        "Spaced repetition for every completed lecture",
        "Daily due list across all chapters",
        "Backlog carry-forward instead of lost revisions",
        "Chapter-wise retention progress",
      ],
      explore: [
        { to: "/revision-tracker", label: "How revision planning works" },
        { to: "/toolkit", label: "Free study toolkit" },
      ],
    },
  },
  {
    prefix: "/questions",
    props: {
      eyebrow: "Question practice tracker",
      title: "Keep your question practice honest",
      description:
        "Set question targets per chapter and log what you actually solved, so under-practised chapters surface before a test does it for you.",
      bullets: [
        "Chapter-wise question targets",
        "Daily practice tasks in your plan",
        "Bookmarks and saved practice history",
        "Weak-chapter insights from your own data",
      ],
      explore: [
        { to: "/study-planner", label: "How the daily plan works" },
        { to: "/simulations", label: "Free simulations" },
      ],
    },
  },
  {
    prefix: "/profile",
    props: {
      eyebrow: "Your account",
      title: "Your profile, streaks and synced progress",
      description:
        "Create a free account to keep your streak, XP, chapters, revisions and test scores synced across every device you study on.",
      bullets: [
        "Progress synced across phone, tablet and laptop",
        "Streaks, XP and level tracking",
        "Your data stays yours, always exportable",
        "Ready for premium features when they launch",
      ],
      explore: [{ to: "/pricing", label: "See pricing" }],
    },
  },
  {
    prefix: "/admin",
    props: {
      eyebrow: "Admin",
      title: "Sign in to continue",
      description: "This area is available to administrators only.",
      bullets: ["Admin sign-in required"],
      ctaLabel: "Sign in",
    },
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { status, profile, error, retry } = useSession();
  const hasStoredSession = useHasStoredSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const queryClient = useQueryClient();
  const overlayOpen = useOverlayOpen();
  const { requirePremium } = usePremium();
  const showQuotaUpgrade = useQuotaUpgrade();
  const [welcomeDone, setWelcomeDone] = useState<boolean | null>(null);
  const [splashDone, setSplashDone] = useState<boolean | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [celebration, setCelebration] = useState<{ title: string; subtitle: string } | null>(null);
  const [toasterReady, setToasterReady] = useState(false);

  useEffect(() => {
    setWelcomeDone(localStorage.getItem(WELCOME_KEY) === "1");
    // The cinematic intro runs at most once per browser session, only on the
    // root route (deep links skip it), and never with reduced motion enabled.
    const alreadyShown = sessionStorage.getItem(SPLASH_KEY) === "1";
    const deepLink = window.location.pathname !== "/";
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    if (alreadyShown || deepLink || reduced) {
      sessionStorage.setItem(SPLASH_KEY, "1");
      setSplashDone(true);
    } else {
      setSplashDone(false);
    }
  }, []);

  // Mount the toast host once the browser is idle, or immediately if something
  // asks for a toast before that.
  useEffect(() => {
    onToastsNeeded(() => setToasterReady(true));
    const arm = () => {
      preloadToasts();
      setToasterReady(true);
    };
    const ric = (window as Window & typeof globalThis).requestIdleCallback;
    if (typeof ric === "function") {
      const id = ric(arm);
      return () => {
        onToastsNeeded(() => {});
        window.cancelIdleCallback?.(id);
      };
    }
    const id = window.setTimeout(arm, 1200);
    return () => {
      onToastsNeeded(() => {});
      window.clearTimeout(id);
    };
  }, []);

  const openAddChapter = useCallback(() => {
    if (!requirePremium()) return;
    setSheetOpen(true);
  }, [requirePremium]);

  const isPublicPage = PUBLIC_ROUTES.includes(pathname.replace(/\/$/, "") || "/");

  const create = useMutation({
    mutationFn: (input: {
      subject: Subject;
      chapter: string;
      totalLectures: number;
      completedOn: string | null;
    }) => addChapter({ data: input }),
    retry: 2,
    // Optimistic: the chapter shows up (and the sheet closes) before the write.
    onMutate: (input) => {
      const stop = trackSave("add-chapter");
      setSheetOpen(false);
      const tempId = `temp-chapter-${Date.now()}`;
      const previous = queryClient.getQueryData<StudyChapter[]>(["chapters"]);
      const lectures = Array.from({ length: input.totalLectures }, (_, i) => ({
        id: `${tempId}-l${i + 1}`,
        number: i + 1,
        completedOn: input.completedOn,
      }));
      const optimistic: StudyChapter = {
        id: tempId,
        subject: input.subject,
        chapter: input.chapter,
        totalLectures: input.totalLectures,
        createdAt: new Date().toISOString(),
        lectures,
        revisions: input.completedOn
          ? lectures.flatMap((lecture) =>
              LECTURE_REVISION_INTERVALS.map((days, index) => ({
                id: `${lecture.id}-r${index + 1}`,
                lectureId: lecture.id,
                lectureNumber: lecture.number,
                revisionNumber: index + 1,
                intervalDays: days,
                dueDate: addDays(input.completedOn as string, days),
                status: "pending" as const,
                completedAt: null,
              })),
            )
          : [],
      };
      queryClient.setQueryData<StudyChapter[]>(["chapters"], (old) => [optimistic, ...(old ?? [])]);
      notifySaved("Saved Successfully");
      setCelebration({
        title: "Chapter added",
        subtitle: input.completedOn
          ? `${input.totalLectures} lectures scheduled for revision`
          : `Tick lectures in ${input.chapter} whenever you finish them`,
      });
      return { previous, stop };
    },
    onError: (err, _input, ctx) => {
      ctx?.stop("error");
      if (ctx?.previous) queryClient.setQueryData(["chapters"], ctx.previous);
      showQuotaUpgrade(err);
    },
    onSuccess: (_res, _input, ctx) => ctx?.stop("ok"),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["chapters"] });
      queryClient.invalidateQueries({ queryKey: ["question-chapters"] });
    },
  });

  if (isPublicPage) {
    return (
      <div className="aurora min-h-[100dvh] pb-16">
        <FloatingProfileHeaderButton />
        {children}
        <GlobalQuoteSection />
        <SiteFooter />
      </div>
    );
  }



  // Signed-out visitors explore the whole site. Personal-data routes render a
  // landing page; the sign-up modal only appears when they try to save.
  // While auth is still resolving and this browser has no stored session, the
  // public home page paints straight away instead of a spinner.
  if (status === "signed_out" || (status === "loading" && !hasStoredSession)) {
    const normalized = pathname.replace(/\/$/, "") || "/";
    const landing = GUEST_LANDINGS.find(
      (l) => normalized === l.prefix || normalized.startsWith(`${l.prefix}/`),
    );
    return (
      <div className="aurora min-h-[100dvh] pb-48 lg:pb-16">
        <DesktopNav />
        {normalized === "/" ? (
          <MarketingHome />
        ) : landing ? (
          <GuestFeatureScreen {...landing.props} />
        ) : (
          children
        )}
        <GlobalQuoteSection />
        <SiteFooter />

        <ThemeToggle
          className={cn(
            "fixed bottom-28 left-5 z-40 size-12 transition-opacity duration-300",
            overlayOpen && "pointer-events-none opacity-0",
          )}
        />
      <BottomNav />
      </div>
    );
  }


  if (splashDone === false) {
    return (
      <SplashScreen
        onDone={() => {
          sessionStorage.setItem(SPLASH_KEY, "1");
          setSplashDone(true);
        }}
      />
    );
  }

  if (status === "error") {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 px-8 text-center">
        <h1 className="text-xl font-bold">We couldn&apos;t start your session</h1>
        <p className="text-sm text-muted-foreground">{error}</p>
        <RippleButton onClick={retry}>Try again</RippleButton>
      </div>
    );
  }

  if (welcomeDone === null || splashDone === null || status === "loading") {
    return (
      <div className="aurora flex min-h-[100dvh] flex-col items-center justify-center gap-4">
        <div className="size-12 animate-spin rounded-full border-2 border-hairline border-t-primary" />
        <p className="text-xs tracking-wide text-muted-foreground">Restoring your progress…</p>
      </div>
    );
  }

  if (!welcomeDone) {
    return (
      <WelcomeScreen
        onStart={() => {
          localStorage.setItem(WELCOME_KEY, "1");
          setWelcomeDone(true);
        }}
      />
    );
  }



  if (!profile) {
    return (
      <div className="aurora flex min-h-[100dvh] items-center justify-center">
        <div className="size-12 animate-spin rounded-full border-2 border-hairline border-t-primary" />
      </div>
    );
  }

  const normalizedPath = pathname.replace(/\/$/, "") || "/";

  return (
    <StudyActionsContext.Provider value={{ openAddChapter }}>
      <InstallPrompt />
      {normalizedPath === "/" ? null : <FloatingProfileHeaderButton />}

      <DesktopNav />

      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        style={{ willChange: "transform, opacity" }}
        className="aurora min-h-[100dvh] pb-48 lg:pb-16"
      >
        {children}
        <GlobalQuoteSection />
        <SiteFooter />
      </motion.div>


      <ThemeToggle
        className={cn(
          "fixed bottom-28 left-5 z-40 size-12 transition-opacity duration-300",
          overlayOpen && "pointer-events-none opacity-0",
        )}
      />

      <FloatingModeButton />

      <BottomNav />

      {toasterReady ? (
        <Suspense fallback={null}>
          <Toaster position="top-center" />
        </Suspense>
      ) : null}

      <AddChapterSheet
        open={sheetOpen}
        busy={false}
        onClose={() => setSheetOpen(false)}
        onSubmit={(input) => create.mutate(input)}
      />

      <Celebration
        open={celebration !== null}
        title={celebration?.title ?? ""}
        subtitle={celebration?.subtitle ?? ""}
        onDone={() => setCelebration(null)}
      />
    </StudyActionsContext.Provider>
  );
}
